/**
 * Probegespräch im Browser — die Telefonleitung, nur ohne Telefon.
 *
 * Der Browser verhält sich hier wie eine Twilio-Media-Stream-Verbindung: er
 * schickt `start`/`media`/`mark`/`stop` und µ-law-Audio mit 8 kHz, und er
 * beantwortet `mark` und `clear` genauso wie Twilio. Dadurch führt der
 * Voice-Server das Gespräch durch EXAKT denselben Anrufweg wie einen echten
 * Anruf: gleicher Prompt, gleiche Salondaten, gleiche Regeln, gleiches
 * Barge-in, gleiche Live-Engine. Ein eigener "Test-Modus" mit ähnlichem
 * Verhalten würde genau das verbergen, was man hier prüfen will.
 *
 * Warum überhaupt über unseren Server (und nicht direkt zu OpenAI): seit dem
 * Hardcut läuft jeder Anruf über `gpt-live-1`. Der Realtime-Transport, den ein
 * Browser ansprechen könnte, kennt dieses Modell nicht (`invalid_model`), und
 * die Live-API akzeptiert weder einen ephemeren Schlüssel noch ein
 * WebSocket-Subprotokoll — nur einen echten API-Schlüssel im Header, der
 * niemals in einen Browser gehört.
 */

import { base64ToBytes, bytesToBase64, floatToMulaw, mulawToFloat } from './mulaw';

/** Telefonrate. Nicht verhandelbar: so spricht die Gegenstelle. */
const TELEFON_RATE = 8000;
/** 20 ms pro Rahmen — dieselbe Paketierung wie am Telefon. */
const FRAME_SAMPLES = 160;

export type ProbeCallState = 'idle' | 'connecting' | 'live' | 'ended' | 'error';

export interface ProbeCallHandlers {
  onState?: (state: ProbeCallState) => void;
  onError?: (message: string) => void;
}

/**
 * Die beiden AudioWorklets. Sie liegen als Quelltext hier und werden zur
 * Laufzeit als Blob geladen — so braucht der Build keine Sonderregel für ein
 * zweites Bundle-Ziel, und die Verarbeitung läuft trotzdem im Audio-Thread
 * (ein ScriptProcessor auf dem Hauptthread knackst, sobald React rendert).
 *
 * Beide rechnen die Gerätefrequenz (meist 48 kHz) auf 8 kHz um: Aufnahme
 * durch Mittelung über die zusammenfallenden Samples, Wiedergabe durch
 * lineare Interpolation.
 */
const WORKLET_QUELLE = `
class ProbeCapture extends AudioWorkletProcessor {
  constructor() {
    super();
    this.schritt = sampleRate / ${TELEFON_RATE};
    this.phase = 0;
    this.summe = 0;
    this.anzahl = 0;
    this.rahmen = new Float32Array(${FRAME_SAMPLES});
    this.n = 0;
  }
  process(inputs) {
    const kanal = inputs[0] && inputs[0][0];
    if (!kanal) return true;
    for (let i = 0; i < kanal.length; i++) {
      this.summe += kanal[i];
      this.anzahl++;
      this.phase++;
      if (this.phase >= this.schritt) {
        this.phase -= this.schritt;
        this.rahmen[this.n++] = this.anzahl > 0 ? this.summe / this.anzahl : 0;
        this.summe = 0;
        this.anzahl = 0;
        if (this.n === ${FRAME_SAMPLES}) {
          this.port.postMessage(this.rahmen.slice());
          this.n = 0;
        }
      }
    }
    return true;
  }
}
registerProcessor('probe-capture', ProbeCapture);

class ProbePlayback extends AudioWorkletProcessor {
  constructor() {
    super();
    // 30 s Vorrat: der Server schickt Antworten schneller als in Echtzeit.
    this.ring = new Float32Array(${TELEFON_RATE} * 30);
    this.schreib = 0;
    this.lese = 0;          // Float: Bruchteile entstehen beim Hochrechnen
    this.vorrat = 0;        // gepufferte Eingangssamples (8 kHz)
    this.schritt = ${TELEFON_RATE} / sampleRate;
    this.gespielt = 0;      // abgespielte Eingangssamples seit dem letzten clear
    this.epoche = 0;        // zählt die clears mit (siehe unten)
    this.meldung = 0;
    this.port.onmessage = (e) => {
      const d = e.data;
      if (d.typ === 'push') {
        const s = d.samples;
        for (let i = 0; i < s.length; i++) {
          this.ring[this.schreib] = s[i];
          this.schreib = (this.schreib + 1) % this.ring.length;
        }
        this.vorrat += s.length;
        if (this.vorrat > this.ring.length) {
          // Überlauf: der älteste Ton ist weg. Lieber hörbar springen als
          // still weiterrechnen — deshalb Lesezeiger nachziehen.
          this.lese = this.schreib;
          this.vorrat = this.ring.length;
        }
      } else if (d.typ === 'clear') {
        // Barge-in: alles Gepufferte verwerfen, wie Twilio es tut. Der
        // verworfene Ton wird nie gespielt — deshalb MUSS auch die Zaehlung
        // bei null anfangen, sonst hinkt der Abspielzaehler dem Empfang fuer
        // den Rest des Gespraechs um die verworfenen Samples hinterher und
        // keine spaetere Marke wuerde je bestaetigt (der Agent kaeme nie
        // zum Auflegen).
        this.lese = this.schreib;
        this.vorrat = 0;
        this.gespielt = 0;
        this.epoche = d.epoche;
      }
    };
  }
  process(_inputs, outputs) {
    const out = outputs[0][0];
    if (!out) return true;
    for (let i = 0; i < out.length; i++) {
      if (this.vorrat < 2) {
        out[i] = 0;
        continue;
      }
      const basis = Math.floor(this.lese);
      const anteil = this.lese - basis;
      const a = this.ring[basis % this.ring.length];
      const b = this.ring[(basis + 1) % this.ring.length];
      out[i] = a + (b - a) * anteil;
      const vorher = Math.floor(this.lese);
      this.lese += this.schritt;
      const verbraucht = Math.floor(this.lese) - vorher;
      if (verbraucht > 0) {
        this.vorrat -= verbraucht;
        this.gespielt += verbraucht;
      }
      if (this.lese >= this.ring.length) this.lese -= this.ring.length;
    }
    // Fortschritt ~alle 40 ms melden: daran hängen die Mark-Bestätigungen.
    this.meldung += out.length;
    if (this.meldung >= sampleRate / 25) {
      this.meldung = 0;
      this.port.postMessage({
        typ: 'fortschritt',
        gespielt: this.gespielt,
        epoche: this.epoche,
      });
    }
    return true;
  }
}
registerProcessor('probe-playback', ProbePlayback);
`;

interface OffeneMarke {
  name: string;
  /** Position im Ausgabestrom (8-kHz-Samples), ab der die Marke erreicht ist. */
  position: number;
}

export class ProbeCall {
  private ws: WebSocket | null = null;
  private ctx: AudioContext | null = null;
  private mikro: MediaStream | null = null;
  private quelle: MediaStreamAudioSourceNode | null = null;
  private aufnahme: AudioWorkletNode | null = null;
  private wiedergabe: AudioWorkletNode | null = null;
  private stumm = false;
  private beendet = false;
  private zustand: ProbeCallState = 'idle';

  /** Empfangene Ausgangssamples seit dem letzten `clear` — Bezug für Marken. */
  private empfangen = 0;
  /**
   * Zählt die `clear`-Ereignisse. Meldungen aus dem Audio-Thread, die noch
   * aus der Zeit VOR einem clear stammen, dürfen keine neue Marke bestätigen
   * — sonst legt der Agent mitten im Satz auf.
   */
  private epoche = 0;
  private offeneMarken: OffeneMarke[] = [];

  constructor(private handlers: ProbeCallHandlers = {}) {}

  get state(): ProbeCallState {
    return this.zustand;
  }

  private setState(next: ProbeCallState) {
    if (this.zustand === next) return;
    this.zustand = next;
    this.handlers.onState?.(next);
  }

  private fail(message: string) {
    if (this.beendet) return;
    this.handlers.onError?.(message);
    this.setState('error');
    this.stop();
  }

  /** Mikrofon holen, Audio aufbauen, Leitung öffnen. */
  async start(wsUrl: string): Promise<void> {
    this.beendet = false;
    this.setState('connecting');

    try {
      this.mikro = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          // Ohne Echo-Unterdrückung hört der Agent sich selbst aus dem
          // Lautsprecher und unterbricht sich — am Telefon gibt es dieses
          // Problem nicht.
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      this.fail(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Kein Zugriff auf das Mikrofon. Bitte im Browser erlauben.'
          : 'Mikrofon konnte nicht geöffnet werden.',
      );
      return;
    }

    try {
      await this.audioAufbauen();
    } catch (err) {
      this.fail('Audio konnte im Browser nicht gestartet werden.');
      return;
    }

    try {
      await this.leitungOeffnen(wsUrl);
    } catch (err) {
      this.fail(err instanceof Error ? err.message : 'Verbindung fehlgeschlagen.');
    }
  }

  private async audioAufbauen(): Promise<void> {
    // 8 kHz direkt anfragen: dann resampelt der Browser selbst und die
    // Worklets arbeiten 1:1. Lehnt er ab (Safari in älteren Fassungen),
    // rechnen die Worklets die Rate selbst um.
    try {
      this.ctx = new AudioContext({ sampleRate: TELEFON_RATE, latencyHint: 'interactive' });
    } catch {
      this.ctx = new AudioContext({ latencyHint: 'interactive' });
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    const blob = new Blob([WORKLET_QUELLE], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    try {
      await this.ctx.audioWorklet.addModule(url);
    } finally {
      URL.revokeObjectURL(url);
    }

    this.quelle = this.ctx.createMediaStreamSource(this.mikro!);
    this.aufnahme = new AudioWorkletNode(this.ctx, 'probe-capture');
    this.aufnahme.port.onmessage = (e) => this.rahmenSenden(e.data as Float32Array);
    // Die Aufnahme braucht ein Ziel, sonst zieht sie in manchen Browsern
    // keine Daten. Mit Verstärkung 0 hört niemand sich selbst.
    const stumm = this.ctx.createGain();
    stumm.gain.value = 0;
    this.quelle.connect(this.aufnahme);
    this.aufnahme.connect(stumm);
    stumm.connect(this.ctx.destination);

    this.wiedergabe = new AudioWorkletNode(this.ctx, 'probe-playback', {
      numberOfInputs: 0,
      outputChannelCount: [1],
    });
    this.wiedergabe.port.onmessage = (e) => {
      const d = e.data as { typ: string; gespielt: number; epoche: number };
      if (d.typ === 'fortschritt' && d.epoche === this.epoche) {
        this.markenBestaetigen(d.gespielt);
      }
    };
    this.wiedergabe.connect(this.ctx.destination);
  }

  private leitungOeffnen(wsUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(wsUrl);
      this.ws = ws;

      const abbruch = window.setTimeout(() => {
        reject(new Error('Zeitüberschreitung beim Verbinden.'));
        ws.close();
      }, 15000);

      ws.onopen = () => {
        window.clearTimeout(abbruch);
        // Dieselbe Eröffnung wie bei Twilio. `customParameters` bleibt leer:
        // Rufnummer und Stimme stehen im Ticket, nicht im Datenstrom.
        ws.send(JSON.stringify({ event: 'connected', protocol: 'Call', version: '1.0.0' }));
        ws.send(
          JSON.stringify({
            event: 'start',
            start: { streamSid: 'probe', callSid: 'probe', customParameters: {} },
          }),
        );
        this.setState('live');
        resolve();
      };

      ws.onmessage = (ev) => this.serverEreignis(ev.data);

      ws.onerror = () => {
        window.clearTimeout(abbruch);
        if (this.zustand === 'connecting') reject(new Error('Verbindung fehlgeschlagen.'));
      };

      ws.onclose = (ev) => {
        window.clearTimeout(abbruch);
        if (this.beendet) return;
        if (ev.code === 4401) {
          this.fail('Die Sitzung ist abgelaufen. Bitte erneut starten.');
          return;
        }
        if (ev.code === 4500) {
          this.fail('Der Gesprächsserver ist nicht verfügbar.');
          return;
        }
        if (this.zustand === 'connecting') {
          // Noch nie verbunden gewesen: das ist ein Fehler, kein Gesprächsende.
          this.fail('Verbindung zum Gesprächsserver nicht möglich.');
          return;
        }
        // Normaler Fall: der Agent hat aufgelegt.
        this.setState('ended');
        this.stop();
      };
    });
  }

  private rahmenSenden(block: Float32Array) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    // Stummschaltung heißt Stille senden, nicht aufhören zu senden: die
    // Live-Engine braucht einen durchgehenden Audiostrom, sonst schweigt sie.
    const bytes = this.stumm ? new Uint8Array(block.length).fill(0xff) : floatToMulaw(block);
    this.ws.send(
      JSON.stringify({ event: 'media', media: { payload: bytesToBase64(bytes) } }),
    );
  }

  private serverEreignis(roh: unknown) {
    if (typeof roh !== 'string') return;
    let daten: { event?: string; media?: { payload?: string }; mark?: { name?: string } };
    try {
      daten = JSON.parse(roh);
    } catch {
      return;
    }

    if (daten.event === 'media' && daten.media?.payload) {
      const samples = mulawToFloat(base64ToBytes(daten.media.payload));
      this.empfangen += samples.length;
      this.wiedergabe?.port.postMessage({ typ: 'push', samples }, [samples.buffer]);
      return;
    }

    if (daten.event === 'clear') {
      // Verworfener Ton wird nie gespielt — also auch nie bestätigt, genau
      // wie bei Twilio. Der Server räumt seine Marken beim `clear` selbst ab.
      // Beide Zähler fangen wieder bei null an, damit spätere Marken auf den
      // neu gepufferten Ton zeigen und nicht auf verworfenen.
      this.epoche += 1;
      this.empfangen = 0;
      this.offeneMarken = [];
      this.wiedergabe?.port.postMessage({ typ: 'clear', epoche: this.epoche });
      return;
    }

    if (daten.event === 'mark' && daten.mark?.name) {
      this.offeneMarken.push({ name: daten.mark.name, position: this.empfangen });
    }
  }

  private markenBestaetigen(gespielt: number) {
    if (!this.offeneMarken.length) return;
    const faellig = this.offeneMarken.filter((m) => gespielt >= m.position);
    if (!faellig.length) return;
    this.offeneMarken = this.offeneMarken.filter((m) => gespielt < m.position);
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    for (const marke of faellig) {
      this.ws.send(JSON.stringify({ event: 'mark', mark: { name: marke.name } }));
    }
  }

  setMuted(muted: boolean) {
    this.stumm = muted;
  }

  get muted(): boolean {
    return this.stumm;
  }

  /** Auflegen und alles freigeben. Mehrfach aufrufbar. */
  stop() {
    if (this.beendet) return;
    this.beendet = true;

    if (this.ws) {
      try {
        if (this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ event: 'stop' }));
        }
        this.ws.onclose = null;
        this.ws.close();
      } catch {
        /* egal — die Leitung ist ohnehin weg */
      }
      this.ws = null;
    }

    this.aufnahme?.port.close();
    this.aufnahme?.disconnect();
    this.wiedergabe?.port.close();
    this.wiedergabe?.disconnect();
    this.quelle?.disconnect();
    this.mikro?.getTracks().forEach((t) => t.stop());
    this.ctx?.close().catch(() => undefined);

    this.aufnahme = null;
    this.wiedergabe = null;
    this.quelle = null;
    this.mikro = null;
    this.ctx = null;
    this.offeneMarken = [];
    this.empfangen = 0;
    this.epoche = 0;

    if (this.zustand !== 'error') this.setState('ended');
  }
}
